import { CareOSLoader } from '@/components/ui/careos-loader';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Check, Loader2, ExternalLink, Crown, Users, UserPlus, Heart, ShieldCheck, Building2 } from 'lucide-react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionRequired } from '@/hooks/useSubscriptionRequired';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { usePatientRole } from '@/hooks/usePatientRole';
import { useCompany } from '@/hooks/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { PaymentIssueBanner } from '@/components/subscription/PaymentIssueBanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { track } from '@/lib/analytics';

// Plan configuration is now dynamic
const FEATURES = [
  'subscription.features.unlimitedPatients',
  'subscription.features.unlimitedCaregivers',
  'subscription.features.realTimeTracking',
  'subscription.features.detailedReports',
  'subscription.features.prioritySupport',
];

type Plan = {
  id: string;
  name: string;
  amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  tiers?: any[];
  tiers_mode?: string;
  metadata?: Record<string, string>;
};

export default function SubscriptionPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { session, subscription, isCheckingSubscription, checkSubscription } = useAuth();
  const { shiftsCount, shiftsLimit, patientsCount, patientsLimit, isOwner: isGlobalOwner, isLoading: isCheckingUsage } = useSubscriptionRequired();
  const { focusedPatient } = useFocusedPatient();
  const { isCompanyMember, isCompanyManager, company } = useCompany();
  const { isOwner: isFocusedOwner, isCaregiver: isFocusedCaregiver, isObserver: isFocusedObserver, role: focusedRole } = usePatientRole(focusedPatient?.id);
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // For company managers, the subscription belongs to the company; usage must
  // reflect company_patient_assignments instead of personal owner links.
  const [companyAssignmentsCount, setCompanyAssignmentsCount] = useState<number>(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!company?.companyId) {
        setCompanyAssignmentsCount(0);
        return;
      }
      const { count } = await supabase
        .from('company_patient_assignments')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', company.companyId)
        .eq('is_active', true);
      if (!cancelled) setCompanyAssignmentsCount(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [company?.companyId, subscription.subscribed, subscription.patientSlots]);

  const useCompanyUsage = isCompanyManager || subscription.isCompanyPlan;
  const displayPatientsCount = useCompanyUsage ? companyAssignmentsCount : patientsCount;
  const displayPatientsLimit = patientsLimit;
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-refresh subscription on landing here. If the user was redirected
  // mistakenly (stale local state, expired session token, webhook lag), this
  // re-queries Stripe via the check-subscription edge function and updates
  // local state — preventing a wrongful "subscribe" prompt for users who
  // already have an active plan.
  useEffect(() => {
    if (!session?.access_token) return;
    if (searchParams.get('checkout')) return; // handled by the effect below
    let cancelled = false;
    (async () => {
      // Initial silent refresh
      await checkSubscription(true);
      if (cancelled) return;
      // If still not subscribed, retry once after a short delay in case the
      // Stripe webhook is still processing or the local cache lagged behind.
      if (!subscription.subscribed) {
        await new Promise(r => setTimeout(r, 2500));
        if (cancelled) return;
        await checkSubscription(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]);

  // Detect checkout result from URL params
  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus === 'success') {
      toast.success(t('subscription.checkoutSuccess', 'Assinatura realizada com sucesso!'));
      // Force refresh subscription status with retries (Stripe may take a moment)
      const refreshWithRetry = async () => {
        for (let i = 0; i < 5; i++) {
          await checkSubscription();
          if (i < 4) await new Promise(r => setTimeout(r, 3000));
        }
      };
      refreshWithRetry();
      // Clean up URL
      setSearchParams({}, { replace: true });
    } else if (checkoutStatus === 'cancelled') {
      toast.info(t('subscription.checkoutCancelled', 'Checkout cancelado.'));
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [isCompanyManager]);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-subscription-plans', {
        body: { type: isCompanyManager ? 'company' : 'family' },
      });
      if (error) throw error;
      if (data?.plans) {
        setPlans(data.plans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error(t('subscription.errors.fetchPlansFailed'));
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const locale = i18n.language === 'pt-BR' ? ptBR : enUS;
  const usagePercentage = Math.min((shiftsCount / shiftsLimit) * 100, 100);
  const isFreeTier = shiftsCount < shiftsLimit;

  const handleSubscribe = async (plan: Plan) => {
    if (!session?.access_token) {
      toast.error(t('subscription.errors.notAuthenticated'));
      return;
    }

    setIsCreatingCheckout(true);
    try {
      // Canonical funnel event — fired BEFORE redirecting to Stripe so we
      // capture intent even if the user abandons the checkout.
      track('subscription_checkout_started', {
        plan_id: plan.id,
        plan_name: plan.name,
        amount: plan.amount,
        currency: plan.currency,
        billing_period: plan.interval,
        billing_interval_count: plan.interval_count,
        is_tiered: !!(plan.tiers && plan.tiers.length > 0),
        audience: isCompanyManager ? 'b2b' : 'b2c',
      });

      const isNative = Capacitor.isNativePlatform();
      const body: any = {};

      if (isNative) {
        body.success_url = 'careos://app/subscription?checkout=success';
        body.cancel_url = 'careos://app/subscription?checkout=cancelled';
      }

      body.priceId = plan.id;

      // Calculate min/max from tiers if available
      if (plan.tiers && plan.tiers.length > 0) {
        // Assuming tiers are sorted, but we can't be sure of the structure perfectly without scanning
        // Usually first tier starts at 1. 
        // If there is a specific logic for "starting at", we might want to set min_quantity.
        // For now, defaulting to 1 as min, and if logical max exists (e.g. last tier up_to is not null?), we use it.
        // But usually last tier up_to is null (infinity).

        body.min_quantity = 1;

        const lastTier = plan.tiers[plan.tiers.length - 1];
        if (lastTier.up_to) {
          body.max_quantity = lastTier.up_to;
        } else {
          body.max_quantity = 9999; // A safe high number for logic
        }
      } else {
        // Default for non-tiered
        body.min_quantity = 1;
        body.max_quantity = 1;
        // Note: if it's not tiered, adjustable_quantity might not be desired? 
        // But the user asked for this in context of tiers. 
        // create-checkout enables adjustable_quantity always currently.
      }


      // Forward coupon code from URL (?coupon=CODE).
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const couponFromUrl = urlParams.get('coupon')?.trim();
        if (couponFromUrl) body.couponId = couponFromUrl;
      } catch { /* noop */ }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body
      });

      if (error) throw error;

      console.log('[handleSubscribe] URL received:', data?.url);
      if (data?.url) {
        const platform = Capacitor.getPlatform();
        const isNative = platform !== 'web';
        console.log('[handleSubscribe] Platform:', platform, 'isNative:', isNative);

        if (isNative) {
          console.log('[handleSubscribe] Opening via Browser.open');
          await Browser.open({ url: data.url });
        } else {
          console.log('[handleSubscribe] Opening via window.open');
          window.open(data.url, '_blank');
        }
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      toast.error(t('subscription.errors.checkoutFailed'));
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!session?.access_token) {
      toast.error(t('subscription.errors.notAuthenticated'));
      return;
    }

    setIsOpeningPortal(true);
    try {
      const isNative = Capacitor.isNativePlatform();
      const body: any = {};

      if (isNative) {
        body.return_url = 'careos://app/subscription';
      }

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body
      });

      if (error) throw error;

      console.log('[handleManageSubscription] URL received:', data?.url);
      if (data?.url) {
        const platform = Capacitor.getPlatform();
        const isNative = platform !== 'web';
        console.log('[handleManageSubscription] Platform:', platform, 'isNative:', isNative);

        if (isNative) {
          console.log('[handleManageSubscription] Opening via Browser.open');
          await Browser.open({ url: data.url });
        } else {
          console.log('[handleManageSubscription] Opening via window.open');
          window.open(data.url, '_blank');
        }
      }
    } catch (err) {
      console.error('Error opening customer portal:', err);
      toast.error(t('subscription.errors.portalFailed'));
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const isSubscribed = subscription.subscribed;

  return (
    <AppLayout>
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <div className="text-center mb-8">
          {isCompanyManager ? (
            <Building2 className="h-12 w-12 mx-auto mb-4 text-primary" />
          ) : (
            <Crown className="h-12 w-12 mx-auto mb-4 text-primary" />
          )}
          <h1 className="text-3xl font-bold mb-2">{t('subscription.title')}</h1>
          <p className="text-muted-foreground">
            {isCompanyManager ? t('subscription.companyDescription') : t('subscription.description')}
          </p>
        </div>

        <div className="mb-6">
          <PaymentIssueBanner />
        </div>

        {/* Managed by Owner card - when focused on a patient where user is caregiver/observer */}
        {!isCheckingUsage && focusedPatient && !isFocusedOwner && focusedRole && (
          <Card className="mb-6 border-muted bg-muted/30">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-6 h-6 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">{focusedPatient.nickname}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('subscription.managedByOwner', { role: t(`roles.${focusedRole}`) })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Your Subscription section header - when global owner and focused on non-owned patient */}
        {isGlobalOwner && focusedPatient && !isFocusedOwner && focusedRole && (
          <h2 className="text-lg font-semibold mb-4">{t('subscription.yourSubscription')}</h2>
        )}

        {/* Current Plan Card - Always visible for owners */}
        {isGlobalOwner && !isCheckingUsage && (
          <>
            {!isSubscribed ? (
              /* FREE TIER card */
              <Card className="mb-6 border-muted">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {t('subscription.currentPlan')}
                    </CardTitle>
                    <Badge variant="secondary">{t('subscription.freeTier.planName')}</Badge>
                  </div>
                  <CardDescription>
                    {t('subscription.freeTier.description', { limit: shiftsLimit })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Usage bars */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{t('subscription.patientsContracted', 'Pacientes contratados')}</span>
                        <span className="font-medium">{displayPatientsLimit}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>{t('subscription.patientsInUse', 'Pacientes em uso')}</span>
                        <span className={`font-medium ${displayPatientsCount > displayPatientsLimit ? 'text-destructive' : ''}`}>{displayPatientsCount}</span>
                      </div>
                      <Progress value={Math.min((displayPatientsCount / displayPatientsLimit) * 100, 100)} className="h-2" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{t('subscription.freeTier.shiftsUsed')}</span>
                        <span className="font-medium">{shiftsCount} / {shiftsLimit}</span>
                      </div>
                      <Progress value={usagePercentage} className="h-2" />
                    </div>

                    {isFreeTier ? (
                      <p className="text-sm text-muted-foreground">
                        {t('subscription.freeTier.remaining', { remaining: shiftsLimit - shiftsCount })}
                      </p>
                    ) : (
                      <p className="text-sm text-destructive font-medium">
                        {t('subscription.freeTier.limitReached')}
                      </p>
                    )}

                    {/* Benefits comparison */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
                      <div>
                        <p className="text-sm font-medium mb-2 text-muted-foreground">{t('subscription.freeTier.included')}</p>
                        <ul className="space-y-1 text-sm">
                          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{t('subscription.freeTier.benefit1')}</li>
                          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{t('subscription.freeTier.benefit2', { limit: shiftsLimit })}</li>
                          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{t('subscription.freeTier.benefit3')}</li>
                          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{t('subscription.freeTier.benefit4')}</li>
                        </ul>
                      </div>
                      <div>
                        <p className="text-sm font-medium mb-2 text-muted-foreground">{t('subscription.proOnly.title')}</p>
                        <ul className="space-y-1 text-sm">
                          <li className="flex items-center gap-2"><Crown className="h-4 w-4 text-amber-500" />{t('subscription.proOnly.benefit1')}</li>
                          <li className="flex items-center gap-2"><Crown className="h-4 w-4 text-amber-500" />{t('subscription.proOnly.benefit2')}</li>
                          <li className="flex items-center gap-2"><Crown className="h-4 w-4 text-amber-500" />{t('subscription.proOnly.benefit3')}</li>
                          <li className="flex items-center gap-2"><Crown className="h-4 w-4 text-amber-500" />{t('subscription.proOnly.benefit4')}</li>
                        </ul>
                      </div>
                    </div>

                    <p className="text-sm font-medium text-center text-primary pt-2">
                      {t('subscription.upgradePrompt')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* PRO TIER card (moved here from below) */
              <Card className={`mb-6 ${subscription.isCompanyPlan ? 'border-info' : 'border-primary'}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {subscription.isCompanyPlan ? (
                          <Building2 className="h-5 w-5 text-info" />
                        ) : (
                          <Heart className="h-5 w-5 text-primary" />
                        )}
                        {subscription.productName ?? t('subscription.currentPlan')}
                        <Badge variant="default" className="ml-2">
                          {t('subscription.activeShort')}
                        </Badge>
                        <Badge variant="outline" className="ml-1">
                          {subscription.isCompanyPlan
                            ? t('subscription.planTypes.company', 'Empresa')
                            : t('subscription.planTypes.family', 'Família')}
                        </Badge>
                      </CardTitle>
                    </div>
                    <CreditCard className="h-10 w-10 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Price block */}
                    {typeof subscription.unitAmount === 'number' && subscription.currency && (
                      <div className="rounded-md border bg-muted/30 p-3">
                        {(() => {
                          const qty = subscription.quantity ?? 1;
                          const total = subscription.unitAmount! * qty;
                          const fmt = (cents: number) =>
                            new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: subscription.currency!,
                            }).format(cents / 100);
                          const intervalLabel =
                            subscription.interval === 'year'
                              ? t('subscription.interval.year')
                              : t('subscription.interval.month');
                          return (
                            <>
                              <div className="text-2xl font-bold">
                                {fmt(total)}
                                <span className="text-sm font-normal text-muted-foreground">
                                  /{intervalLabel}
                                </span>
                              </div>
                              {qty > 1 && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {qty} × {fmt(subscription.unitAmount!)}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {t('subscription.patientsContracted', 'Pacientes contratados')}
                        </span>
                        <span className="font-medium">{displayPatientsLimit}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>{t('subscription.patientsInUse', 'Pacientes em uso')}</span>
                        <span className={`font-medium ${displayPatientsCount > displayPatientsLimit ? 'text-destructive' : ''}`}>{displayPatientsCount}</span>
                      </div>
                      <Progress value={Math.min((displayPatientsCount / displayPatientsLimit) * 100, 100)} className="h-2" />
                      {displayPatientsCount > displayPatientsLimit && (
                        <p className="text-xs text-destructive">
                          {t('subscription.overLimitWarning', 'Você excedeu o limite contratado. Ajuste a quantidade de pacientes na sua assinatura.')}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {t('subscription.patientSlotsHint')}
                      </p>
                    </div>

                    {subscription.subscriptionEnd && (
                      <p className="text-sm text-muted-foreground">
                        {t('subscription.renewsAt')}{' '}
                        <span className="font-medium">
                          {format(new Date(subscription.subscriptionEnd), 'PPP', { locale })}
                        </span>
                      </p>
                    )}
                    <Button
                      onClick={handleManageSubscription}
                      disabled={isOpeningPortal}
                      variant="outline"
                      className="w-full"
                    >
                      {isOpeningPortal ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ExternalLink className="h-4 w-4 mr-2" />
                      )}
                      {t('subscription.manageSubscription')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Caregiver Promo - shown for non-owners */}
        {!isCheckingUsage && !isGlobalOwner && (
          <Card className="mb-6 border-primary/30 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">{t('subscription.caregiverPromo.title')}</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('subscription.caregiverPromo.description')}
                  </p>
                  <Button size="sm" onClick={() => navigate('/app/patients/new')}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('subscription.caregiverPromo.cta')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {isLoadingPlans ? (
          <div className="flex items-center justify-center py-8">
            <CareOSLoader size="md" />
          </div>
        ) : (() => {
          // When user is already subscribed, hide any offer card that matches
          // the current subscription by price ID OR product ID (avoids
          // duplication with the "current plan" card above even when the
          // price_id returned by check-subscription doesn't exactly match the
          // catalog entry, e.g. legacy graduated price vs flat price).
          const visiblePlans = isSubscribed
            ? plans.filter((p: any) => {
                const samePrice = subscription.priceId && p.id === subscription.priceId;
                const sameProduct = subscription.productId && p.product_id === subscription.productId;
                return !samePrice && !sameProduct;
              })
            : plans;
          if (visiblePlans.length === 0) return null;

          return (
          <>
          {isSubscribed && (
            <h2 className="text-lg font-semibold mb-4">
              {t('subscription.otherPlans', 'Outros planos')}
            </h2>
          )}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {visiblePlans.map((plan) => {
              const hasTiers = plan.tiers && plan.tiers.length > 0;
              // Get lowest price for "Starting at"
              const lowestPrice = hasTiers
                ? Math.min(...plan.tiers.map((t: any) => t.unit_amount || t.flat_amount || 0))
                : plan.amount;

              return (
                <Card
                  key={plan.id}
                  className="flex flex-col hover:border-primary/50 transition-all w-full md:w-[350px]"
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        {plan.interval === 'month' ? t('subscription.monthly') : t('subscription.yearly')}
                      </CardTitle>
                      {!hasTiers && (
                        <Badge variant="secondary" className="text-xs">
                          {t('subscription.freeTier.benefit1', 'Até 2 pacientes')}
                        </Badge>
                      )}
                    </div>

                    <div className="mt-2">
                      <CardDescription className="font-bold text-xl text-foreground">
                        {hasTiers && <span className="text-xs font-normal text-muted-foreground block mb-1">{t('subscription.tiers.startingAt')}</span>}
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: plan.currency }).format(lowestPrice / 100)}
                        <span className="text-sm font-normal text-muted-foreground">
                          {hasTiers
                            ? `${t('subscription.tiers.perUnit')}/${plan.interval === 'month' ? t('subscription.interval.month') : t('subscription.interval.year')}`
                            : `/${plan.interval === 'month' ? t('subscription.interval.month') : t('subscription.interval.year')}`
                          }
                        </span>
                      </CardDescription>

                      {hasTiers && (
                        <div className="mt-4 space-y-2 border-t pt-2">
                          <p className="text-xs font-medium text-muted-foreground mb-2">{t('subscription.tiers.title')}</p>
                          {plan.tiers.map((tier: any, idx: number) => {
                            const prevUpTo = idx > 0 ? plan.tiers[idx - 1].up_to : 0;
                            const rangeStart = prevUpTo + 1;
                            return (
                              <div key={idx} className="flex justify-between text-xs text-muted-foreground">
                                <span>
                                  {tier.up_to
                                    ? `${rangeStart} – ${tier.up_to} ${t('subscription.tiers.perUnit', 'vidas').replace('/ ', '')}`
                                    : t('subscription.tiers.rest', { count: prevUpTo })
                                  }
                                </span>
                                <span className="font-medium text-foreground">
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: plan.currency }).format((tier.unit_amount || 0) / 100)}
                                  {t('subscription.tiers.perUnit')}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="mt-auto pt-4">
                    <Button
                      onClick={() => handleSubscribe(plan)}
                      disabled={isCreatingCheckout || isSubscribed}
                      className="w-full"
                      variant={isSubscribed ? "outline" : "default"}
                    >
                      {isCreatingCheckout ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4 mr-2" />
                      )}
                      {isSubscribed ? t('subscription.active') : t('subscription.subscribe')}
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </div>
          </>
          );
        })()}


        {/* Refresh button */}
        <div className="text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              setIsRefreshing(true);
              try {
                await checkSubscription();
                await queryClient.invalidateQueries({ queryKey: ['subscription-required'] });
                if (company?.companyId) {
                  const { count } = await supabase
                    .from('company_patient_assignments')
                    .select('id', { count: 'exact', head: true })
                    .eq('company_id', company.companyId)
                    .eq('is_active', true);
                  setCompanyAssignmentsCount(count ?? 0);
                }
                toast.success(t('subscription.refreshed', 'Status atualizado'));
              } finally {
                setIsRefreshing(false);
              }
            }}
            disabled={isCheckingSubscription || isRefreshing}
          >
            {(isCheckingSubscription || isRefreshing) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('subscription.refreshStatus')}
          </Button>
        </div>

        {/* Back to app */}
        <div className="text-center mt-6">
          <Button variant="link" onClick={() => navigate('/app')}>
            {t('common.back')}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
